/**
 * Date helpers for the task views.
 *
 * The server stores every timestamp as UTC and stays timezone-agnostic; the
 * client decides what "today" means and sends explicit UTC bounds. That keeps
 * the boundary logic in one place — the place that actually knows the user's
 * timezone — instead of guessing it server-side.
 */

export function startOfDay(date: Date = new Date()): Date {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
}

export function addDays(date: Date, days: number): Date {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
}

/** Exclusive upper bound for "today" — i.e. the first instant of tomorrow. */
export function startOfTomorrow(now: Date = new Date()): Date {
  return addDays(startOfDay(now), 1);
}

/**
 * The quick-capture date presets.
 *
 * `null` means "no due date" and is a real choice, not an absence — picking it
 * clears a date the text parser guessed at.
 */
export const DUE_PRESETS = ['today', 'tomorrow', 'next_week', 'none'] as const;
export type DuePreset = (typeof DUE_PRESETS)[number];

export const DUE_PRESET_LABELS: Record<DuePreset, string> = {
  today: 'Today',
  tomorrow: 'Tomorrow',
  next_week: 'Next week',
  none: 'No date',
};

/** Time of day a bare date means. Matches what the text parser uses. */
const MORNING = 9;
/** "Today" means before the working day ends, not first thing this morning. */
const END_OF_WORKDAY = 17;

export function duePreset(preset: DuePreset, now: Date = new Date()): Date | null {
  switch (preset) {
    case 'none':
      return null;

    case 'today': {
      // 17:00 rather than 09:00: a task added at 11am and marked "today" would
      // otherwise be born overdue. "Tomorrow" and later mean "from that
      // morning", which is why only this case differs.
      const d = startOfDay(now);
      d.setHours(END_OF_WORKDAY);
      return d;
    }

    case 'tomorrow': {
      const d = startOfDay(addDays(now, 1));
      d.setHours(MORNING);
      return d;
    }

    case 'next_week': {
      // The coming Monday. From a Monday that means the NEXT one, not today —
      // "next week" never means "right now".
      const d = startOfDay(now);
      const daysUntilMonday = ((8 - d.getDay()) % 7) || 7;
      d.setDate(d.getDate() + daysUntilMonday);
      d.setHours(MORNING);
      return d;
    }
  }
}

/** Which preset, if any, a date corresponds to — used to light up a chip. */
export function matchDuePreset(due: Date | null, now: Date = new Date()): DuePreset | null {
  if (due === null) return 'none';
  for (const preset of DUE_PRESETS) {
    const candidate = duePreset(preset, now);
    if (candidate && candidate.getTime() === due.getTime()) return preset;
  }
  return null;
}

export function isSameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

export function isOverdue(dueAt: string | null, now: Date = new Date()): boolean {
  return dueAt !== null && new Date(dueAt).getTime() < now.getTime();
}

/**
 * Short human label for a due date: "Today", "Tomorrow", "3d overdue",
 * "Fri 14 Aug". Deliberately relative for the near term — that is the
 * information you act on — and absolute once it is far enough away.
 */
export function formatDue(dueAt: string | null, now: Date = new Date()): string {
  if (!dueAt) return '';

  const due = new Date(dueAt);
  const today = startOfDay(now);
  const dueDay = startOfDay(due);
  const dayDiff = Math.round((dueDay.getTime() - today.getTime()) / 86_400_000);

  const time = due.getHours() === 0 && due.getMinutes() === 0 ? '' : ` ${formatTime(due)}`;

  if (dayDiff === 0) return `Today${time}`;
  if (dayDiff === 1) return `Tomorrow${time}`;
  if (dayDiff === -1) return `Yesterday${time}`;
  if (dayDiff < -1) return `${Math.abs(dayDiff)}d overdue`;
  if (dayDiff < 7) {
    return `${due.toLocaleDateString(undefined, { weekday: 'short' })}${time}`;
  }

  return due.toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    ...(due.getFullYear() !== now.getFullYear() ? { year: 'numeric' } : {}),
  });
}

/**
 * Absolute short date. Used where relative wording would lie — a completed
 * task must not read "5d overdue" just because its due date has passed.
 */
export function formatDateShort(iso: string | null, now: Date = new Date()): string {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    ...(d.getFullYear() !== now.getFullYear() ? { year: 'numeric' } : {}),
  });
}

export function formatTime(date: Date): string {
  return date.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
}

/** `2026-08-05T14:30` — the format a datetime-local input expects. */
export function toDateTimeLocal(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** Converts a datetime-local value back to a UTC ISO string. */
export function fromDateTimeLocal(value: string): string | null {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}
